import * as React from 'react';
import { faArrowDown, faArrowUp, faEquals } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, IconButton, styled } from '@mend/mui';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, Card, Stack, Tooltip, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router';

import { useNavigationStore } from '#stores/navigation.ts';

interface StatWidgetData {
  id: string;
  title: string;
  value: number | null;
  valueFormatted?: string;
  valueType: 'percentage' | 'absolute';
  trend?: {
    rawValue: number;
    direction: 'up' | 'down' | 'neutral';
    comparisonPeriod: string;
    /** The inclusive amount of days in the period. */
    daysInPeriod: number;
    dataCompleteness: number;
  };
}

interface StatBaseProps {
  /**
   * The ID of the widget. It is passed to the `data-testid` attribute of the
   * root element.
   */
  id?: string;
  data: StatWidgetData;
  infoTemplate?: React.ReactNode;
  drilldownRoutePath?: string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: '100%',
  position: 'relative',
  boxShadow: theme.shadows[2],
  borderRadius: Number(theme.shape.borderRadius) * 2,
}));

const getTrendColor = (direction: 'up' | 'down' | 'neutral') => {
  if (direction === 'up') return 'success.main';
  if (direction === 'down') return 'error.main';
  return 'text.secondary';
};

const formatTrendValue = (rawValue: number): string => {
  const formattedValue = (rawValue * 100).toFixed(1);
  return rawValue > 0 ? `+${formattedValue}%` : `${formattedValue}%`;
};

export function StatBase(props: StatBaseProps) {
  const { id, data, infoTemplate, drilldownRoutePath } = props;

  const location = useLocation();
  const setNavigationState = useNavigationStore((state) => state.setState);

  const [isInfoDialogOpen, setIsInfoDialogOpen] = React.useState(false);

  const hasNoData = data.value === null;
  const hasInsufficientPriorData = !data.trend || (!hasNoData && data.trend.dataCompleteness < 0.8);
  const hasPartialPriorData =
    !hasNoData &&
    data.trend?.dataCompleteness !== undefined &&
    data.trend.dataCompleteness >= 0.8 &&
    data.trend.dataCompleteness < 1;

  const getDisplayValue = (): string => {
    if (hasNoData) return '--';
    if (data.valueFormatted) return data.valueFormatted;
    if (data.valueType === 'percentage') {
      return `${Math.round(data.value as number)}%`;
    }
    return String(data.value);
  };

  const handleCloseInfoDialog = () => {
    setIsInfoDialogOpen(false);
  };

  return (
    <>
      <StyledCard data-testid={id}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 200,
          }}
        >
          <Stack alignItems="center" sx={{ mb: 2 }}>
            <Typography component="h2" variant="subtitle1">
              {data.title}
            </Typography>
            {infoTemplate && (
              <IconButton onClick={() => setIsInfoDialogOpen(true)}>
                <InfoIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {hasNoData ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No data available for the selected date range
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" component="p" sx={{ mb: 1 }}>
                {getDisplayValue()}
              </Typography>

              {hasInsufficientPriorData ? (
                <Typography variant="body2" color="text.secondary">
                  Not enough prior data to calculate trend
                </Typography>
              ) : data.trend ? (
                <Tooltip title={data.trend.comparisonPeriod} placement="top">
                  <Stack
                    direction="row"
                    alignItems="center"
                    // So the tooltip is properly centered on the text
                    display="inline-flex"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: getTrendColor(data.trend.direction),
                      }}
                    >
                      <FontAwesomeIcon
                        icon={
                          data.trend.direction === 'up'
                            ? faArrowUp
                            : data.trend.direction === 'down'
                              ? faArrowDown
                              : faEquals
                        }
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {data.trend.direction === 'neutral'
                        ? 'no significant change'
                        : `${formatTrendValue(data.trend.rawValue)}`}{' '}
                      from previous{' '}
                      {data.trend.daysInPeriod === 1 ? 'day' : `${data.trend.daysInPeriod} days`}
                    </Typography>
                  </Stack>
                </Tooltip>
              ) : null}

              {hasPartialPriorData && data.trend && (
                <Typography
                  component="p"
                  variant="caption"
                  color="error.main"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Trend based on partial data
                </Typography>
              )}
            </Box>
          )}

          {drilldownRoutePath && (
            <Button
              component={Link}
              size="small"
              color="info"
              variant="text"
              sx={{ alignSelf: 'flex-end' }}
              to={drilldownRoutePath}
              onClick={() => setNavigationState('/', location.search)}
              aria-label={`View ${data.title} details`}
            >
              View Details
            </Button>
          )}
        </Box>
      </StyledCard>

      {infoTemplate && isInfoDialogOpen && (
        <Dialog.Root open onClose={handleCloseInfoDialog} fullWidth>
          <Dialog.Header>
            <Dialog.Title>{data.title}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Content>{infoTemplate}</Dialog.Content>
        </Dialog.Root>
      )}
    </>
  );
}
